import React, { useMemo } from 'react';
import type { Test } from '@mariana/labos-client';
import { WorksheetViewer } from '../WorksheetViewer';
import { SpreadsheetTable } from '../SpreadsheetTable';
import { formatAnalyticalValue } from '../../utils/formatValue';
import { formatDate } from '../../utils/formatDate';
import { icpDilutionLabel, type IcpXmlEntry } from '../../utils/icpXmlData';
import './ResultViewers.css';

interface ICPResultViewerProps {
  test: Test;
}

const ICP_ELEMENTS = [
  'Al', 'As', 'B', 'Ba', 'Be', 'Br', 'Ca', 'Cd', 'Cl', 'Co',
  'Cr', 'Cu', 'Fe', 'I', 'K', 'Li', 'Mg', 'Mn', 'Na', 'Ni',
  'P', 'Pb', 'S', 'Sb', 'Si', 'Sn', 'Sr', 'Ti', 'Zn',
];

export const ICPResultViewer: React.FC<ICPResultViewerProps> = ({ test }) => {
  const worksheetData = test.attributes?.worksheet_data as Record<string, any> | undefined;
  const rawWorksheetData = test.attributes?.raw_worksheet_data as Record<string, Record<string, any>> | undefined;
  const icpXmlData = test.attributes?.icp_xml_data as IcpXmlEntry[] | undefined;

  const runRows = useMemo(() => {
    if (!Array.isArray(icpXmlData) || icpXmlData.length === 0) return [];
    return icpXmlData.map((entry, i) => {
      const method = [entry.methodName, entry.methodVersion != null ? `v${entry.methodVersion}` : '']
        .filter(Boolean)
        .join(' ');
      return [
        icpDilutionLabel(entry, i),
        entry.measurementDate ? formatDate(entry.measurementDate) : '-',
        entry.calculationDate ? formatDate(entry.calculationDate) : '-',
        method || '-',
      ] as (string | number | null)[];
    });
  }, [icpXmlData]);

  const reportedRows = useMemo(() => {
    if (!worksheetData) return [];
    const hasStandardElements = ICP_ELEMENTS.some(
      el => worksheetData[`report_export_${el.toLowerCase()}`] !== undefined,
    );
    if (hasStandardElements) {
      return ICP_ELEMENTS.map(el => {
        const key = `report_export_${el.toLowerCase()}`;
        const entry = worksheetData[key];
        const value = entry?.value ?? entry;
        return [el, formatAnalyticalValue(value)] as [string, string];
      });
    }
    // No standard element keys (e.g. calibration worksheets report per
    // wavelength line): list every report_export_* entry instead.
    return Object.keys(worksheetData)
      .filter(key => key.startsWith('report_export_'))
      .sort()
      .map(key => {
        const label = key
          .replace('report_export_', '')
          .replace(/^./, c => c.toUpperCase());
        const entry = worksheetData[key];
        const value = entry?.value ?? entry;
        const unit = entry?.unit ? ` ${entry.unit}` : '';
        return [label, `${formatAnalyticalValue(value)}${unit}`] as [string, string];
      });
  }, [worksheetData]);

  return (
    <div className="rv-container">
      {runRows.length > 0 && (
        <div className="rv-section rv-section-runs">
          <h3 className="rv-section-title">Measurement Runs</h3>
          <SpreadsheetTable
            headers={['Dilution', 'Measurement Date', 'Calculation Date', 'Method']}
            rows={runRows}
            showRowNumbers={false}
          />
        </div>
      )}

      {rawWorksheetData && (
        <div className="rv-section rv-section-raw">
          <h3 className="rv-section-title">Raw Worksheet</h3>
          <WorksheetViewer rawWorksheetData={rawWorksheetData} />
        </div>
      )}

      {worksheetData && reportedRows.length > 0 && (
        <div className="rv-section rv-section-reported">
          <h3 className="rv-section-title">Reported Values</h3>
          <SpreadsheetTable
            headers={['Element', 'Value']}
            rows={reportedRows}
            showRowNumbers={false}
          />
        </div>
      )}

      {!worksheetData && !rawWorksheetData && runRows.length === 0 && (
        <div className="rv-empty">No ICP results available for this test.</div>
      )}
    </div>
  );
};
